/**
 * Karbon360° v2 — charts.
 *
 * Hand-rolled SVG rather than a charting library: the app already carries both
 * recharts and chart.js, and these three shapes are simple enough that adding a
 * third dependency would cost more than it saves.
 *
 * Colour rule from the design spec: scope colours are categorical; green and red
 * are reserved for semantics (under/over target, pass/fail) and never used
 * decoratively.
 */
import React, { useId, useState } from 'react';

export const SCOPE_COLORS = {
  scope1: '#6366F1',
  scope2: '#0EA5E9',
  scope3: '#F59E0B',
  biogenic: '#10B981',
};

const fmtN = (n, d = 2) =>
  Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });

/* ── donut ───────────────────────────────────────────────────────────── */
export function Donut({ data, centreLabel = 'Total', centreUnit = 'tCO₂e', size = 190, thickness = 26 }) {
  const total = data.reduce((s, d) => s + (Number(d.value) || 0), 0);
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`${centreLabel} by scope`}>
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          {total === 0 ? (
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--n100)" strokeWidth={thickness} />
          ) : (
            data.map((d) => {
              const frac = (Number(d.value) || 0) / total;
              const len = frac * c;
              const el = (
                <circle
                  key={d.name}
                  cx={size / 2} cy={size / 2} r={r}
                  fill="none" stroke={d.color} strokeWidth={thickness}
                  strokeDasharray={`${len} ${c - len}`}
                  strokeDashoffset={-offset}
                >
                  <title>{`${d.name}: ${fmtN(d.value)} tCO₂e (${(frac * 100).toFixed(1)}%)`}</title>
                </circle>
              );
              offset += len;
              return el;
            })
          )}
        </g>
        <text x="50%" y="47%" textAnchor="middle" style={{ fontSize: 24, fontWeight: 600, fill: 'var(--n900)' }} className="k2-num">
          {fmtN(total)}
        </text>
        <text x="50%" y="60%" textAnchor="middle" style={{ fontSize: 11, fill: 'var(--n500)' }}>
          {centreUnit}
        </text>
      </svg>

      <div style={{ display: 'grid', gap: 8, minWidth: 170, flex: 1 }}>
        {data.map((d) => (
          <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
            <span style={{ width: 9, height: 9, borderRadius: 2.5, background: d.color, flexShrink: 0 }} />
            <span style={{ color: 'var(--n600)', flex: 1, minWidth: 0 }}>{d.name}</span>
            <span className="k2-num" style={{ fontWeight: 550 }}>{fmtN(d.value)}</span>
            <span style={{ color: 'var(--n400)', fontSize: 11, minWidth: 38, textAlign: 'right' }} className="k2-num">
              {total ? `${((d.value / total) * 100).toFixed(0)}%` : '—'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── monthly bars with an optional target line ───────────────────────── */
export function MonthlyBars({ data, height = 190, barColor = SCOPE_COLORS.scope2, showTarget = true }) {
  const id = useId();
  const [hover, setHover] = useState(null);
  const max = Math.max(...data.map((d) => Math.max(Number(d.current) || 0, Number(d.target) || 0)), 0.0001);
  const W = 100;
  const slot = W / Math.max(data.length, 1);
  const barW = slot * 0.52;

  return (
    <div>
      <svg viewBox={`0 0 ${W} 46`} preserveAspectRatio="none" style={{ width: '100%', height, display: 'block' }} role="img" aria-label="Monthly emissions">
        {[0.25, 0.5, 0.75, 1].map((g) => (
          <line key={g} x1="0" x2={W} y1={40 - g * 38} y2={40 - g * 38} stroke="var(--n100)" strokeWidth="0.25" />
        ))}
        {data.map((d, i) => {
          const x = i * slot + (slot - barW) / 2;
          const h = ((Number(d.current) || 0) / max) * 38;
          const th = ((Number(d.target) || 0) / max) * 38;
          return (
            <g key={`${d.month}-${i}`} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
              <rect x={i * slot} y={0} width={slot} height={40} fill={hover === i ? 'var(--k2-hover)' : 'transparent'} />
              <rect x={x} y={40 - h} width={barW} height={h} fill={barColor} rx="0.6">
                <title>{`${d.month}: ${fmtN(d.current, 3)} tCO₂e`}</title>
              </rect>
              {showTarget && Number(d.target) > 0 && (
                <line
                  x1={x - barW * 0.16} x2={x + barW * 1.16}
                  y1={40 - th} y2={40 - th}
                  stroke="var(--n900)" strokeWidth="0.55" strokeLinecap="round"
                />
              )}
            </g>
          );
        })}
        <line x1="0" x2={W} y1="40" y2="40" stroke="var(--n200)" strokeWidth="0.3" />
        <defs><clipPath id={id}><rect width={W} height="46" /></clipPath></defs>
      </svg>
      <div style={{ display: 'flex', marginTop: 6 }}>
        {data.map((d, i) => (
          <div key={`${d.month}-lbl-${i}`} style={{ flex: 1, textAlign: 'center', fontSize: 10.5, color: hover === i ? 'var(--n900)' : 'var(--n400)' }}>
            {d.month}
          </div>
        ))}
      </div>
      {hover !== null && data[hover] && (
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--n600)' }} className="k2-num">
          {data[hover].month}: <b style={{ color: 'var(--n900)' }}>{fmtN(data[hover].current, 3)}</b> tCO₂e
          {showTarget && Number(data[hover].target) > 0 && <> · target {fmtN(data[hover].target, 3)}</>}
        </div>
      )}
    </div>
  );
}

/* ── site × month coverage heatmap ───────────────────────────────────── */
const MONTH_INITIALS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export function Heatmap({ sites, onCell }) {
  if (!sites || sites.length === 0) {
    return <div style={{ fontSize: 12.5, color: 'var(--n500)', padding: '8px 0' }}>No facilities in the boundary yet.</div>;
  }

  const cellStyle = (state) => {
    if (state === 'complete') return { background: 'var(--brand-700)' };
    if (state === 'partial') return { background: 'var(--brand-100)' };
    return { background: 'var(--n50)', border: '1px dashed var(--n300)' };
  };

  return (
    <div>
      <div style={{ display: 'grid', gap: 3 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '150px repeat(12, minmax(0,1fr))', gap: 3 }}>
          <span />
          {MONTH_INITIALS.map((m, i) => (
            <span key={i} style={{ fontSize: 10, color: 'var(--n400)', textAlign: 'center', textTransform: 'uppercase' }}>{m}</span>
          ))}
        </div>
        {sites.map((s) => (
          <div key={s.id} style={{ display: 'grid', gridTemplateColumns: '150px repeat(12, minmax(0,1fr))', gap: 3, alignItems: 'center' }}>
            <span style={{ fontSize: 11.5, color: 'var(--n700)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingRight: 8 }} title={s.name}>
              {s.name}
            </span>
            {s.months.map((m) => (
              <button
                key={m.month}
                type="button"
                onClick={() => onCell && onCell(s, m)}
                title={`${s.name} · ${MONTH_NAMES[m.month - 1]} — ${m.state === 'complete' ? 'Scope 1 & 2 recorded' : m.state === 'partial' ? 'partial (one scope only)' : 'no data'}${m.records ? ` · ${m.records} record${m.records > 1 ? 's' : ''}` : ''}`}
                style={{
                  height: 22, borderRadius: 4, cursor: 'pointer', border: 'none',
                  transition: 'transform .1s', padding: 0, ...cellStyle(m.state),
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.14)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
              />
            ))}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', fontSize: 11, color: 'var(--n500)', marginTop: 12 }}>
        {[
          ['Scope 1 & 2 recorded', { background: 'var(--brand-700)' }],
          ['Partial — one scope only', { background: 'var(--brand-100)' }],
          ['No data', { background: 'var(--n50)', border: '1px dashed var(--n300)' }],
        ].map(([label, st]) => (
          <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, ...st }} />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}
